import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Tooltip,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import getSymbolFromCurrency from 'currency-symbol-map';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useState } from 'react';
import languages from '../../../../../../helpers/utils/languages';
import useTranslate from '../../../../../../hooks/utils/useTranslate';
import OrderCancelIcon from '../../../../../svg-icons/OrderCancelIcon';

const CancelOrderConfirmModal = ({
  isOpen,
  handleClose,
  handleConfirm,
  cancelInfos,
  setCancelInfos,
  targetLanguage,
  confirmLoading,
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [emailPreviewed, setEmailPreviewed] = useState(
    cancelInfos.emailType === 'manual'
  );
  const [translateLoading, setTranslateLoading] = useState(false);
  const { translateText } = useTranslate({
    content: cancelInfos.template || { content: { type: 'doc' } },
    target: 'EN',
  });

  const handlePreviewEmail = async () => {
    // traduire le template
    if (targetLanguage !== 'FR' && !cancelInfos?.templateTranslated) {
      setTranslateLoading(true);
      const targetCode = languages.find(
        (lang) => lang?.flag === targetLanguage?.toLowerCase()
      )?.code2;
      const { translatedText } = await translateText({
        content: cancelInfos.template,
        target: targetCode || 'EN',
      });
      setCancelInfos((state) => ({
        ...state,
        templateTranslated: true,
        template: translatedText,
      }));
      setTranslateLoading(false);
      setCurrentTab(1);
      setEmailPreviewed(true);
    } else {
      setCurrentTab(1);
      setEmailPreviewed(true);
    }
  };

  const handleCancel = () => {
    setCurrentTab(0);
    setEmailPreviewed(false);
    handleClose();
  };

  const dangerousHTML = cancelInfos?.template?.content
    ?.map((block) => {
      if (block.type === 'paragraph') {
        if (block.content) {
          if (block.content[0]?.type === 'image') {
            return `<div style="text-align: ${block.attrs.textAlign}">
              <img src="${block.content[0].attrs.src}" 
                   alt="${block.content[0].attrs.alt}" 
                   title="${block.content[0].attrs.title}"
                   width="${block.content[0].attrs.width}"
              />
            </div>`;
          }
          return `<div style="text-align: ${block.attrs.textAlign}">
            ${block.content.map((content) => content.text).join('')}
          </div>`;
        }
        return '<br/>';
      }
      return '';
    })
    .join('');

  return (
    <Dialog open={isOpen} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle
        sx={[
          {
            borderBottom: 1,
            marginBottom: 3,
            display: 'flex',
            gap: 4,
            alignItems: 'center',
            fontSize: 24,
            color: currentTab === 0 ? 'error.main' : 'primary.main',
          },
        ]}
      >
        {currentTab === 0 ? (
          <>
            <OrderCancelIcon fontSize="large" />
            Confirmation de la demande d&apos;annulation
          </>
        ) : (
          <>
            <VisibilityIcon fontSize="large" color="primary" />
            Aperçu de l&apos;email de confirmation
          </>
        )}
      </DialogTitle>
      <DialogContent
        sx={{
          height: '75vh',
        }}
      >
        {currentTab === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert
              variant="outlined"
              severity="warning"
              sx={{
                backgroundColor: 'rgb(255, 246, 242)',
              }}
            >
              <AlertTitle>Attention</AlertTitle>
              Cette action est irréversible, une fois la demande
              d&apos;annulation confirmée, la commande sera annulée et le
              montant restant remboursé. Prenez les précautions nécessaires
              avant de confirmer la demande d&apos;annulation.
            </Alert>
            <Typography variant="h6" color="primary">
              Informations de la commande
            </Typography>

            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}
            >
              <Typography>
                <strong>Numéro de commande:</strong> {cancelInfos?.orderRef}
              </Typography>

              <Typography>
                <strong>Statut:</strong> {cancelInfos?.orderStatus}
              </Typography>

              <Typography>
                <strong>Montant à rembourser:</strong>{' '}
                {cancelInfos?.refundAmount}
                {getSymbolFromCurrency(cancelInfos?.currency)}
              </Typography>

              <Divider sx={{ my: 1 }} />

              <Typography>
                <strong>Client:</strong> {cancelInfos?.firstName}{' '}
                {cancelInfos?.lastName}
              </Typography>

              <Typography>
                <strong>Store:</strong> {cancelInfos?.store}
              </Typography>

              <Divider sx={{ my: 1 }} />

              <Typography>
                <strong>Motif d&apos;annulation:</strong> {cancelInfos?.reason}
              </Typography>

              <Typography>
                <strong>Commentaire:</strong>
              </Typography>
              <Typography
                sx={{
                  fontStyle: 'italic',
                  padding: 2,
                  border: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: 1,
                }}
              >
                {cancelInfos?.comment}
              </Typography>
              <Button
                onClick={handlePreviewEmail}
                variant="contained"
                color="primary"
                endIcon={<VisibilityIcon />}
                sx={{ mt: 2, width: 'fit-content', alignSelf: 'center' }}
                disabled={translateLoading}
                loading={translateLoading}
              >
                Aperçu{' '}
                {cancelInfos.emailType === 'manual' ? 'du brouillon ' : ''} de
                l&apos;email
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                pb: 1,
                mb: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>De :</strong> support@hoortrade.com
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>À :</strong> {cancelInfos?.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Date :</strong>{' '}
                {format(new Date(), 'dd MMMM yyyy à HH:mm', { locale: fr })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Objet :</strong> Confirmation d&apos;annulation de votre
                commande {cancelInfos?.orderRef}
              </Typography>
            </Box>

            {/* Corps de l'email */}
            <Box
              sx={{
                backgroundColor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                minHeight: '300px',
                maxHeight: '500px',
                overflow: 'auto',
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  display: 'block',
                  margin: '0 auto',
                },
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: dangerousHTML }} />
            </Box>

            {/* Informations supplémentaires */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                * Cet aperçu montre comment l&apos;email apparaîtra au client.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        {currentTab === 0 ? (
          <>
            <Button
              variant="outlined"
              color="info"
              onClick={handleCancel}
              sx={{ marginRight: 2 }}
            >
              Annuler
            </Button>
            <Tooltip
              title={
                !emailPreviewed
                  ? "Visualisez l'aperçu de l'email de confirmation avant de confirmer la demande d'annulation"
                  : ''
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleConfirm}
                  endIcon={
                    confirmLoading ? (
                      <CircularProgress size={10} />
                    ) : (
                      <CheckIcon />
                    )
                  }
                  disabled={
                    (cancelInfos.emailType === 'manual'
                      ? false
                      : !emailPreviewed) || confirmLoading
                  }
                >
                  Confirmer la demande
                </Button>
              </span>
            </Tooltip>
          </>
        ) : (
          <Button
            onClick={() => setCurrentTab(0)}
            variant="contained"
            color="primary"
            sx={{ mt: 2, width: 'fit-content', alignSelf: 'end' }}
            startIcon={<ArrowBackIcon />}
          >
            Retour
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CancelOrderConfirmModal;
